import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Avatar, StatusBadge, Drawer } from '@/components/ui';
import { PortalLinkButton } from '@/components/ui/PortalLinkButton';
import { Progress } from '@/components/ui/Chart';
import { adminApi } from '@/shared/api/adminApi';
import { useMonitorStream } from '@/shared/hooks/useMonitorStream';
import { fmtDuration } from '@/shared/hooks/useTicker';
import { useAuthStore } from '@/shared/stores/auth';
import { canView } from '@/shared/lib/permissions';
import { CountUp } from '@/components/ui/CountUp';
import { useEntrance } from '@/shared/hooks/useCountUp';
import { HeroChart } from './dashboard/HeroChart';
import { Donut } from './dashboard/Donut';
import type { DonutSeg } from './dashboard/Donut';
import './dashboard/dashboard.css';
import { Bar, CARD_STYLE, Delta, KpiCell, SectionTitle, pct, shekels } from './dashboard/parts';

interface QueueRow { id: number; name: string; ext: string; sla_sec: number; sla_pct: number | null; calls_today: number; answered_today: number; waiting_now?: number }
interface LiveAgent { id: number; status: string; name: string; extension?: string; on_call?: boolean }
interface Revenue {
  deals: number;
  revenue_agorot: number;
  avg_deal_agorot: number;
  by_department?: { department_id: number | null; department_name: string | null; deals: number; revenue_agorot: number }[];
  by_agent?: { agent_id: number | null; agent_name: string | null; deals: number; revenue_agorot: number }[];
}
interface DashboardData {
  as_of?: string;
  kpis: {
    calls_today: number; answered_today: number; missed_today: number; agents_active: number; queues: number;
    answer?: { dialed: number; answered: number; answer_rate: number | null };
    voicemail_today?: number; tech_failed_today?: number;
  };
  kpis_delta?: { calls_today: number | null; answered_today: number | null; missed_today: number | null };
  hourly_load: number[];
  queues: QueueRow[];
  presence: Record<string, number>;
  live_agents: LiveAgent[];
  // מגיע רק מה-REST (/admin/dashboard), לא ממסגרות ה-SSE.
  revenue?: Revenue;
}
interface QueueCall { id: number; from: string; to: string; status: string; started_at: string; wait_sec: number | null; talk_sec: number | null; disposition: string | null }

// צבעי פילוח. הגוונים הקשיחים לקוחים כמו שהם מקובץ העיצוב.
const SPLIT_COLORS = ['var(--accent)', '#6E8FB5', '#B39A6E', 'var(--busy)', 'var(--faint)'];

export function AdminDashboard() {
  const { data, connected } = useMonitorStream<DashboardData>('/admin/dashboard', 5000);
  const [drill, setDrill] = useState<{ queue: QueueRow; calls: QueueCall[] } | null>(null);
  const [restRevenue, setRestRevenue] = useState<Revenue | null>(null);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  // 0→1 פעם אחת ב-mount (animT בעיצוב) — פסי המשפך והנציגים גדלים. עדכון חי לא מאפס.
  const grow = useEntrance('dashboard');

  // המחזור לא נשלח בזרם החי — נמשך מה-REST של הדשבורד פעם בדקה.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const d = (await adminApi.dashboard()) as DashboardData | null;
        if (!cancelled && d?.revenue) setRestRevenue(d.revenue);
      } catch { /* נשארים עם הקודם */ }
    };
    void load();
    const h = window.setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(h); };
  }, []);

  async function openQueue(q: QueueRow) {
    try {
      const calls = (await adminApi.queueCalls(q.id)) as QueueCall[];
      setDrill({ queue: q, calls });
    } catch {
      setDrill({ queue: q, calls: [] });
    }
  }

  if (!data?.kpis) return <Card>טוען…</Card>;

  const k = data.kpis;
  const dialed = k.answer?.dialed ?? k.calls_today;
  const answered = k.answer?.answered ?? k.answered_today;
  const answerRate = k.answer?.answer_rate ?? (dialed > 0 ? Math.round((answered / dialed) * 1000) / 10 : null);
  const voicemail = k.voicemail_today ?? 0;
  const techFailed = k.tech_failed_today ?? 0;
  const revenue = data.revenue ?? restRevenue;
  const empty = k.calls_today === 0;
  const asOf = data.as_of ? new Date(data.as_of) : null;

  // תוצאות השיחות: "ללא מענה" הוא כל מה שחויג ולא נענה, לא תא קולי ולא הוחמץ.
  const noAnswer = Math.max(0, dialed - answered - voicemail - k.missed_today);
  const outcomeSegs: DonutSeg[] = [
    { key: 'answered', label: 'נענו', value: answered, color: 'var(--accent)' },
    { key: 'missed', label: 'הוחמצו', value: k.missed_today, color: '#6E8FB5' },
    { key: 'noanswer', label: 'ללא מענה', value: noAnswer, color: '#A9BCD3' },
    { key: 'voicemail', label: 'תא קולי', value: voicemail, color: '#CBD5E1' },
    { key: 'tech', label: 'כשל טכני', value: techFailed, color: 'var(--danger)' },
  ].filter((g) => g.value > 0 || g.key === 'answered');

  const bucket = (a: LiveAgent) => (a.on_call || a.status === 'oncall' ? 'oncall' : ['available', 'acw', 'offline'].includes(a.status) ? a.status : 'away');
  const countBy = (b: string) => data.live_agents.filter((a) => bucket(a) === b).length;
  const agentSegs: DonutSeg[] = [
    { key: 'available', label: 'זמינים', value: countBy('available'), color: 'var(--ok)' },
    { key: 'oncall', label: 'בשיחה', value: countBy('oncall'), color: 'var(--accent)' },
    { key: 'acw', label: 'אחרי שיחה', value: countBy('acw'), color: 'var(--warn)' },
    { key: 'away', label: 'בהפסקה', value: countBy('away'), color: 'var(--busy)' },
    { key: 'offline', label: 'מנותקים', value: countBy('offline'), color: 'var(--faint)' },
  ];

  const deals = revenue?.deals ?? null;
  const funnel = [
    { label: 'שיחות', value: dialed, step: null as number | null, color: 'var(--text-2)' },
    { label: 'נענו', value: answered, step: pct(answered, dialed), color: 'var(--accent)' },
    ...(deals !== null ? [{ label: 'עסקאות', value: deals, step: pct(deals, answered), color: 'var(--ok)' }] : []),
  ];
  const depts = (revenue?.by_department ?? []).filter((d) => d.revenue_agorot > 0 || d.deals > 0);
  const deptTotal = depts.reduce((s, d) => s + (d.revenue_agorot || d.deals), 0);
  const leaders = [...(revenue?.by_agent ?? [])].sort((a, b) => b.deals - a.deals || b.revenue_agorot - a.revenue_agorot).slice(0, 5);
  const leaderMax = Math.max(1, ...leaders.map((l) => l.deals));

  return (
    <div className="stack" style={{ padding: 'var(--gap)', gap: 18 }}>
      {/* סרגל כלים: מצב חיבור ופורטל בצד אחד, "נכון ל-" בצד השני */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 14px',
              background: 'var(--surface)', color: connected ? 'var(--ok)' : 'var(--muted)',
              border: '1px solid var(--glass-line)', borderRadius: 10,
              fontSize: 13, fontWeight: 600, boxShadow: 'var(--shadow-1)',
            }}
          >
            {connected && <span className="ping-dot" />}
            {connected ? 'חי · היום' : 'מתעדכן · היום'}
          </span>
          <PortalLinkButton />
        </span>
        {asOf && (
          <span style={{ fontSize: 12.5, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>
            הנתונים נכונים ל-{asOf.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>

      {/* Hero: שיחות היום + עומס שעתי + KPI */}
      <section className="card" style={{ ...CARD_STYLE, '--d': '0s', padding: 'clamp(20px, 4vw, 30px) clamp(16px, 4vw, 32px) clamp(18px, 4vw, 26px)', display: 'flex', flexDirection: 'column', gap: 22, overflow: 'hidden' } as CSSProperties}>
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {/* גוונים קשיחים כמו בעיצוב (conic-gradient של הבועות) */}
          <div className="dk-blob" style={{ top: -120, insetInlineStart: -60, width: 380, height: 300, background: 'conic-gradient(from 200deg, var(--accent), #7FB6E8, #9AA7D8, var(--accent))', filter: 'blur(46px)' }} />
          <div className="dk-blob b2" style={{ bottom: -160, insetInlineEnd: -80, width: 340, height: 280, background: 'conic-gradient(from 20deg, #8FB8DF, var(--accent), #B8C4D6, #8FB8DF)', filter: 'blur(56px)' }} />
        </div>
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 13, color: 'var(--text-2)' }}>שיחות · היום</span>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'clamp(56px, 8vw, 84px)', fontWeight: 300, lineHeight: 0.95, letterSpacing: '-3.5px', fontVariantNumeric: 'tabular-nums' }}>
              <CountUp value={dialed} />
            </span>
            {data.kpis_delta?.calls_today != null && data.kpis_delta.calls_today !== 0 && (
              <span className="anim-pop" style={{
                whiteSpace: 'nowrap', fontSize: 13, fontWeight: 600, padding: '3px 10px', borderRadius: 99,
                color: data.kpis_delta.calls_today > 0 ? 'var(--ok)' : 'var(--danger)',
                background: data.kpis_delta.calls_today > 0 ? 'var(--ok-soft)' : 'var(--danger-soft)',
              }}>
                {data.kpis_delta.calls_today > 0 ? '▲' : '▼'} {Math.abs(data.kpis_delta.calls_today)}% מאתמול באותה שעה
              </span>
            )}
          </div>
        </div>
        <div style={{ position: 'relative' }}>
          {empty ? (
            <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)', padding: '40px 0', textAlign: 'center' }}>אין שיחות היום עדיין.</div>
          ) : (
            <HeroChart hours={data.hourly_load} />
          )}
        </div>
        <div style={{ position: 'relative', height: 1, background: 'var(--glass-line-2)' }} />
        <div style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', rowGap: 18 }}>
          <KpiCell delay={0.15} label="נענו" value={<CountUp value={answered} />} foot={<Delta value={data.kpis_delta?.answered_today} />} />
          <KpiCell delay={0.22} label="אחוז מענה" value={answerRate === null ? '—' : <CountUp value={answerRate} format={(v) => `${Math.round(v * 10) / 10}%`} />} foot={voicemail > 0 ? <span style={{ color: 'var(--muted)' }}>{voicemail} לתא קולי</span> : undefined} />
          <KpiCell delay={0.29} label="הוחמצו" value={<CountUp value={k.missed_today} />} foot={<Delta value={data.kpis_delta?.missed_today} higherIsBad />} />
          <KpiCell delay={0.36} label="נציגים פעילים" value={<CountUp value={k.agents_active} />} />
          <KpiCell delay={0.43} label="תורי שירות" value={<CountUp value={k.queues} />} />
        </div>
      </section>

      {/* כסף · היום */}
      {revenue && (
        <section className="card" style={{ ...CARD_STYLE, '--d': '.08s', display: 'flex', flexDirection: 'column', gap: 22 } as CSSProperties}>
          <SectionTitle
            title="כסף · היום"
            dot="var(--ok)"
            end={canView(user, 'billing') ? (
              <button type="button" onClick={() => navigate('/admin/billing')} style={{ border: 0, background: 'transparent', color: 'var(--accent-ink)', cursor: 'pointer', fontSize: 13, fontWeight: 500, fontFamily: 'inherit' }}>
                לפיננסים
              </button>
            ) : undefined}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: 18 }}>
            <KpiCell delay={0.15} label="הכנסות" value={<CountUp value={revenue.revenue_agorot} format={shekels} />} size={48} flex="1.4 1 220px" />
            <KpiCell delay={0.22} label="עסקאות שנסגרו" value={<CountUp value={revenue.deals} />} size={32} />
            <KpiCell delay={0.29} label="עסקה ממוצעת" value={revenue.deals > 0 ? <CountUp value={revenue.avg_deal_agorot} format={shekels} /> : '—'} size={32} />
            <KpiCell
              delay={0.36}
              label="אחוז המרה"
              value={answered > 0 ? <CountUp value={(revenue.deals / answered) * 100} format={(v) => `${Math.round(v * 10) / 10}%`} /> : '—'}
              size={32}
              foot={<span style={{ color: 'var(--muted)' }}>{revenue.deals} עסקאות מתוך {answered} שנענו</span>}
            />
          </div>
        </section>
      )}

      {/* מהחיוג ועד העסקה + הנציגים המובילים */}
      <section className="card" style={{ ...CARD_STYLE, '--d': '.12s', display: 'flex', flexWrap: 'wrap', gap: '32px 48px' } as CSSProperties}>
        <div style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <SectionTitle title="מהחיוג ועד העסקה" sub="כמה נשארים בכל שלב" />
          {funnel.map((f) => (
            <div key={f.label} className="hover-row" style={{ display: 'grid', gridTemplateColumns: '52px minmax(0,1fr) auto', gap: 14, alignItems: 'center', padding: '6px 10px', margin: '-6px -10px', borderRadius: 8 }}>
              <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{f.label}</span>
              <Bar pct={dialed > 0 ? (f.value / dialed) * 100 * grow : 0} color={f.color} />
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 84, justifyContent: 'flex-end' }}>
                <span style={{ fontSize: 17, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}><CountUp value={f.value} /></span>
                <span style={{ fontSize: 12, color: 'var(--muted)', minWidth: 38, textAlign: 'end' }}>{f.step !== null ? `${f.step}%` : ''}</span>
              </div>
            </div>
          ))}
          {depts.length > 1 && deptTotal > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
              <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>הכנסות לפי מחלקה</span>
              <div style={{ display: 'flex', height: 8, borderRadius: 99, overflow: 'hidden', gap: 3 }}>
                {depts.map((d, i) => (
                  <div key={d.department_id ?? 'none'} style={{ height: '100%', borderRadius: 99, background: SPLIT_COLORS[i % SPLIT_COLORS.length], flex: d.revenue_agorot || d.deals }} />
                ))}
              </div>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {depts.map((d, i) => (
                  <span key={d.department_id ?? 'none'} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 99, background: SPLIT_COLORS[i % SPLIT_COLORS.length] }} />
                    {d.department_name ?? 'ללא מחלקה'}
                    <b style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{pct(d.revenue_agorot || d.deals, deptTotal)}%</b>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        {revenue && (
          <div style={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ marginBottom: 10 }}>
              <SectionTitle title="הנציגים המובילים" sub="לפי עסקאות שנסגרו היום" dot="var(--ok)" />
            </div>
            {leaders.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין עסקאות היום עדיין.</div>}
            {leaders.map((l, i) => (
              <div key={l.agent_id ?? `none-${i}`} className="dk-leader" style={{ display: 'grid', gridTemplateColumns: '22px minmax(0,1fr) auto', gap: 14, alignItems: 'center', padding: '9px 10px', margin: '0 -10px', borderRadius: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.agent_name ?? 'ללא נציג'}</span>
                  <Bar pct={(l.deals / leaderMax) * 100 * grow} height={3} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.25 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{l.deals}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{shekels(l.revenue_agorot)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* דונאטים */}
      <section className="card" style={{ ...CARD_STYLE, '--d': '.18s', display: 'flex', flexWrap: 'wrap', gap: '36px 56px' } as CSSProperties}>
        <div style={{ flex: '1 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <SectionTitle title="תוצאות השיחות" sub="מה קרה בכל שיחה היום" />
          <Donut segs={outcomeSegs} centerLabel="שיחות" />
        </div>
        <div style={{ flex: '1 1 280px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
          <SectionTitle title="מצב הנציגים" sub="איפה הצוות נמצא עכשיו" dot="#6E8FB5" />
          <Donut segs={agentSegs} centerLabel="נציגים" />
        </div>
      </section>

      {/* נציגים חיים + תורי שירות */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 18 }}>
        <section className="card" style={{ ...CARD_STYLE, '--d': '.24s', padding: 'clamp(16px, 4vw, 24px) clamp(16px, 4vw, 24px) 16px', display: 'flex', flexDirection: 'column', gap: 12 } as CSSProperties}>
          <SectionTitle
            title="נציגים חיים"
            dot="var(--ok)"
            end={<span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{data.live_agents.length} נציגים</span>}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, margin: '0 -12px' }}>
            {data.live_agents.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)', padding: '10px 12px' }}>אין נציגים מחוברים.</div>}
            {data.live_agents.slice(0, 12).map((a, i) => {
              const inCall = bucket(a) === 'oncall';
              return (
              <div key={a.id} className="dash-row anim-row" style={{ '--d': `${0.1 + i * 0.035}s`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '9px 12px' } as CSSProperties}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  {/* טבעת פועמת סביב נציג בשיחה (gcRing) */}
                  <span className={inCall ? 'anim-ring' : undefined} style={{ display: 'inline-flex', borderRadius: '50%', flexShrink: 0 }}>
                    <Avatar name={a.name ?? '?'} idx={a.id} size={36} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{a.name}</div>
                    <div dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', textAlign: 'end' }}>ext {a.extension ?? '—'}</div>
                  </div>
                </div>
                {inCall ? (
                  <StatusBadge tone="info" size="sm"><span className="eq" aria-hidden="true"><i /><i /><i /></span>{labelFor('oncall')}</StatusBadge>
                ) : a.status === 'offline' ? (
                  <StatusBadge tone="neutral" size="sm">
                    <span className="anim-blink" aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', animationDuration: '1.2s' }} />
                    {labelFor(a.status)}
                  </StatusBadge>
                ) : (
                  <StatusBadge tone={statusTone(a.status)} dot size="sm">{labelFor(a.status)}</StatusBadge>
                )}
              </div>
              );
            })}
          </div>
        </section>

        <section className="card" style={{ ...CARD_STYLE, '--d': '.3s', padding: 'clamp(16px, 4vw, 24px) clamp(16px, 4vw, 24px) 20px', display: 'flex', flexDirection: 'column', gap: 12 } as CSSProperties}>
          <SectionTitle title="תורי שירות" sub="לחצו על תור לשיחות שלו היום" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.queues.length === 0 && <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>אין תורי שירות.</div>}
            {data.queues.map((q, i) => (
              <button
                key={q.id}
                type="button"
                className="queue-tile dk-enter hover-lift"
                onClick={() => openQueue(q)}
                style={{ '--d': `${0.1 + i * 0.07}s`, transition: 'transform .3s var(--ease-out), box-shadow .3s ease, border-color .3s ease', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, width: '100%', padding: 14, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'start', color: 'inherit' } as CSSProperties}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{q.name}</span>
                    <span dir="ltr" style={{ fontSize: 'var(--t-xs)', background: 'var(--surface-3)', color: 'var(--text-2)', padding: '2px 6px', borderRadius: 'var(--r-1)' }}>ext {q.ext}</span>
                  </div>
                  <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                    {q.answered_today}/{q.calls_today} נענו היום
                    {(q.waiting_now ?? 0) > 0 && <span className="anim-blink" style={{ color: 'var(--warn)' }}> · {q.waiting_now} ממתינים</span>}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, minWidth: 100 }}>
                  {q.sla_pct === null ? (
                    <span style={{ fontSize: 'var(--t-xs)', fontWeight: 600, color: 'var(--muted)' }}>אין נתונים</span>
                  ) : (
                    <div style={{ width: 100 }}>
                      <Progress value={q.sla_pct} max={100} color={q.sla_pct >= 80 ? 'var(--ok)' : q.sla_pct >= 50 ? 'var(--warn)' : 'var(--danger)'} />
                    </div>
                  )}
                  <span dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
                    {q.sla_pct === null ? `SLA ${q.sla_sec}s` : `${q.sla_pct}% / SLA ${q.sla_sec}s`}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </section>
      </div>

      <Drawer isOpen={!!drill} onClose={() => setDrill(null)} title={drill ? `שיחות בתור ${drill.queue.name} (היום)` : ''}>
        {drill && (drill.calls.length === 0 ? (
          <div style={{ color: 'var(--muted)' }}>אין שיחות היום בתור זה.</div>
        ) : (
          <div className="stack" style={{ gap: 4 }}>
            {drill.calls.map((c) => (
              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 8px', background: 'var(--surface-2)', borderRadius: 'var(--r-2)', fontSize: 'var(--t-sm)' }}>
                <span dir="ltr">{c.from}</span>
                <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>
                  {labelFor(c.status)}{c.wait_sec != null ? ` · המתנה ${fmtDuration(c.wait_sec)}` : ''}{c.talk_sec ? ` · שיחה ${fmtDuration(c.talk_sec)}` : ''}
                </span>
              </div>
            ))}
          </div>
        ))}
      </Drawer>
    </div>
  );
}

function statusTone(s: string) {
  return s === 'available' ? 'ok' : s === 'oncall' ? 'info' : s === 'acw' ? 'warn' : s === 'offline' ? 'neutral' : 'busy';
}
function labelFor(s: string) {
  return ({ available: 'זמין', oncall: 'בשיחה', acw: 'אחרי שיחה', break: 'הפסקה', lunch: 'ארוחת צהריים', training: 'הכשרה', offline: 'מנותק', ringing: 'מצלצל', completed: 'הושלמה', missed: 'הוחמצה', answered: 'נענתה' } as Record<string, string>)[s] ?? s;
}
